import { styled } from '@material-ui/core';

import { useEditorState } from '@tiptap/react';

import { Select } from '@backstage/ui';

import { useCurrentEditor } from './Context';
import { closeAllDropdowns } from './useDropdownGroup';

const StyledSelect = styled(Select)({
  '& .bui-SelectTrigger': {
    border: 'none',
  },
});

export const BlockTypeSelect = () => {
  const { editor } = useCurrentEditor();

  const currentNodeType = useEditorState({
    editor,
    selector: ctx => {
      if (!ctx.editor) return 'paragraph';
      if (ctx.editor.isActive('heading', { level: 1 })) return 'h1';
      if (ctx.editor.isActive('heading', { level: 2 })) return 'h2';
      if (ctx.editor.isActive('heading', { level: 3 })) return 'h3';
      if (ctx.editor.isActive('heading', { level: 4 })) return 'h4';
      if (ctx.editor.isActive('blockquote')) return 'blockquote';
      if (ctx.editor.isActive('codeBlock')) return 'code-block';
      if (ctx.editor.isActive('bulletList')) return 'bullet-list';
      if (ctx.editor.isActive('orderedList')) return 'ordered-list';
      if (ctx.editor.isActive('calloutBlock')) return 'callout-block';
      return 'paragraph';
    },
  });

  // Merge multi-paragraph selections into a single block
  const convertSelectionToCodeBlock = () => {
    if (!editor) return;

    const { from, to } = editor.state.selection;
    const text = editor.state.doc.textBetween(from, to, '\n');

    editor
      .chain()
      .focus()
      .deleteRange({ from, to })
      .insertContent({
        type: 'codeBlock',
        content: text ? [{ type: 'text', text }] : undefined,
      })
      .run();
  };

  const handleNodeTypeChange = (value: string) => {
    if (!editor) return;

    if (value === 'paragraph') {
      editor.chain().focus().setParagraph().run();
    } else if (value.startsWith('h')) {
      const level = parseInt(value.substring(1), 10) as 1 | 2 | 3 | 4;
      editor.chain().focus().setHeading({ level }).run();
    } else if (value === 'blockquote') {
      editor.chain().focus().toggleBlockquote().run();
    } else if (value === 'code-block') {
      convertSelectionToCodeBlock();
    } else if (value === 'bullet-list') {
      editor.chain().focus().toggleBulletList().run();
    } else if (value === 'ordered-list') {
      editor.chain().focus().toggleOrderedList().run();
    } else if (value === 'callout-block') {
      editor.chain().focus().toggleCalloutBlock().run();
    }
  };

  return (
    <StyledSelect
      aria-label="Block type"
      name="node-type"
      value={currentNodeType}
      onChange={value => handleNodeTypeChange(String(value))}
      onOpenChange={isOpen => isOpen && closeAllDropdowns()}
      options={[
        { label: 'Paragraph', value: 'paragraph' },
        { label: 'Heading 1', value: 'h1' },
        { label: 'Heading 2', value: 'h2' },
        { label: 'Heading 3', value: 'h3' },
        { label: 'Heading 4', value: 'h4' },
        { label: 'Block', value: 'callout-block' },
        { label: 'Blockquote', value: 'blockquote' },
        { label: 'Bullet list', value: 'bullet-list' },
        { label: 'Ordered list', value: 'ordered-list' },
        { label: 'Code block', value: 'code-block' },
      ]}
    />
  );
};
